import React from "react";
import { Icon, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import MapIcon from "@material-ui/icons/Map";
import EventNoteIcon from "@material-ui/icons/EventNote";
import { getTrendMeta } from "../../pages/wqScorecard/buildScorecardTable";
import {
  ATTAINMENT_LABELS,
  BAND_LABELS,
  BAND_SCALE_NOTE,
  MARKER_LEGEND,
  STANDARD_SCALE_NOTE,
} from "../../pages/wqScorecard/scorecardCopy";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(4),
    marginTop: theme.spacing(2),
  },
  group: {
    minWidth: 220,
    maxWidth: 420,
  },
  scaleNote: {
    marginTop: theme.spacing(1),
    fontSize: "0.72rem",
    lineHeight: 1.45,
    color: theme.palette.text.secondary,
  },
  title: {
    fontWeight: 600,
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  items: {
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  item: {
    display: "flex",
    alignItems: "center",
    minHeight: 22,
    gap: 8,
    marginBottom: 4,
    fontSize: "0.82rem",
  },
  swatch: {
    width: 18,
    height: 18,
    borderRadius: 3,
    border: "1px solid rgba(0, 0, 0, 0.25)",
    flexShrink: 0,
  },
  dot: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    border: "1.5px solid #333",
    flexShrink: 0,
  },
  trendIcon: {
    fontSize: "1.25rem",
    flexShrink: 0,
    // MUI's Icon is a 1em box with overflow hidden; the wider ligatures
    // (trending_up, swap_vert) get their edges shaved off inside it.
    overflow: "visible",
    width: "1.25em",
    textAlign: "center",
  },
  marker: {
    display: "inline-block",
    minWidth: 36,
    textAlign: "center",
    fontSize: "0.78rem",
    flexShrink: 0,
  },
  markerMuted: {
    fontStyle: "italic",
    opacity: 0.72,
  },
  markerDash: {
    opacity: 0.35,
  },
  markerIcon: {
    fontSize: "1rem",
    verticalAlign: "middle",
    opacity: 0.7,
  },
}));

// The trends the scorecard can show, worst-to-best order left as the order a
// reader scans: direction first, then the two no-direction outcomes.
const TREND_KEYS = [
  "Increasing",
  "Probably Increasing",
  "Stable",
  "No Trend",
  "Probably Decreasing",
  "Decreasing",
  "No Current Trend",
];

// Which band color each attainment outcome borrows; mirrors ATTAINMENT_SCALE
// in buildScorecardTable so the legend swatch matches the cell.
const ATTAINMENT_ROWS = [
  { key: "attaining", scale: 1 },
  { key: "not_attaining", scale: 4 },
  { key: "insufficient", scale: -1 },
];

/**
 * The legends side by side: the benchmark bands, straight from the same color
 * list the map's legend uses; the attainment colors for the regulatory
 * columns when any are on screen; the trend icons; and the cell markers.
 * Each entry pairs the visual with its name, so neither color nor icon has to
 * be interpreted alone.
 */
export default function ScorecardLegend({
  benchmarkColors = [],
  showStandards = false,
  showMarkers = true,
}) {
  const classes = useStyles();

  // Worst at the top reads more naturally in a legend than the raw -1..4 order.
  const bands = [...benchmarkColors].sort(
    (a, b) => Number(b.bmk_value) - Number(a.bmk_value)
  );
  const colorByScale = new Map(
    benchmarkColors.map((c) => [Number(c.bmk_value), c])
  );

  return (
    <div className={classes.root}>
      <div className={classes.group}>
        <Typography component="h3" className={classes.title}>
          Benchmark bands
        </Typography>
        <ul className={classes.items}>
          {bands.map((band) => (
            <li key={band.bmk_value} className={classes.item}>
              <span
                className={classes.swatch}
                style={{ backgroundColor: band.graph_background_color }}
                aria-hidden="true"
              />
              <span
                className={classes.dot}
                style={{ backgroundColor: band.dot_symbol_color }}
                aria-hidden="true"
              />
              <span>
                {BAND_LABELS[String(band.bmk_value)] ?? band.legend_text}
              </span>
            </li>
          ))}
        </ul>
        <Typography className={classes.scaleNote}>{BAND_SCALE_NOTE}</Typography>
      </div>

      {showStandards && (
        <div className={classes.group}>
          <Typography component="h3" className={classes.title}>
            Reservoir standards
          </Typography>
          <ul className={classes.items}>
            {ATTAINMENT_ROWS.map(({ key, scale }) => {
              const color = colorByScale.get(scale);
              return (
                <li key={key} className={classes.item}>
                  <span
                    className={classes.swatch}
                    style={{
                      backgroundColor: color?.graph_background_color,
                    }}
                    aria-hidden="true"
                  />
                  <span>{ATTAINMENT_LABELS[key]}</span>
                </li>
              );
            })}
          </ul>
          <Typography className={classes.scaleNote}>
            {STANDARD_SCALE_NOTE}
          </Typography>
        </div>
      )}

      <div className={classes.group}>
        <Typography component="h3" className={classes.title}>
          Trends
        </Typography>
        <ul className={classes.items}>
          {TREND_KEYS.map((key) => {
            const meta = getTrendMeta(key);
            return (
              <li key={key} className={classes.item}>
                <Icon className={classes.trendIcon} aria-hidden="true">
                  {meta.icon}
                </Icon>
                <span>{meta.description}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {showMarkers && (
        <div className={classes.group}>
          <Typography component="h3" className={classes.title}>
            Markers
          </Typography>
          <ul className={classes.items}>
            <li className={classes.item}>
              <span
                className={`${classes.marker} ${classes.markerMuted}`}
                aria-hidden="true"
              >
                n = 3
              </span>
              <span>{MARKER_LEGEND.smallSample}</span>
            </li>
            <li className={classes.item}>
              <span className={classes.marker} aria-hidden="true">
                n/a
              </span>
              <span>{MARKER_LEGEND.noData}</span>
            </li>
            <li className={classes.item}>
              <span
                className={`${classes.marker} ${classes.markerDash}`}
                aria-hidden="true"
              >
                {"—"}
              </span>
              <span>{MARKER_LEGEND.dash}</span>
            </li>
            <li className={classes.item}>
              <span className={classes.marker} aria-hidden="true">
                <EventNoteIcon className={classes.markerIcon} />
              </span>
              <span>{MARKER_LEGEND.calendar}</span>
            </li>
            <li className={classes.item}>
              <span className={classes.marker} aria-hidden="true">
                <MapIcon className={classes.markerIcon} />
              </span>
              <span>{MARKER_LEGEND.map}</span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
